import { alpha, createTheme, PaletteMode } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface Palette {
    button: {
      darker: string;
      dark: string;
    };
    table: {
      head: string;
      row: string;
    };
    /**
     * Brand colour for *text* on page/paper surfaces. `primary.main` only
     * reaches 4.03:1 on the dark background, so accent labels use a lighter
     * tint there and a darker one on light surfaces.
     */
    accentText: string;
    /**
     * Semantic colours for *text* sitting on a tinted pill over a page surface.
     * The vivid `*.main` hues clear 4.5:1 on neither white nor near-black, so
     * labels use a darker tint on light and a lighter one on dark.
     */
    semanticText: {
      success: string;
      error: string;
      warning: string;
      info: string;
    };
  }
  interface PaletteOptions {
    button?: {
      darker: string;
      dark: string;
    };
    table?: {
      head: string;
      row: string;
    };
    accentText?: string;
    semanticText?: {
      success: string;
      error: string;
      warning: string;
      info: string;
    };
  }
}

const brand = {
  primary: "#635BFF",
  primaryLight: "#8B84FF",
  primaryDark: "#4B43D7",
  accent: "#16B8C4",
};

const buildTheme = (mode: PaletteMode) => {
  const isDark = mode === "dark";
  const background = isDark ? "#0B1020" : "#F3F5FA";
  const paper = isDark ? "#12192B" : "#FFFFFF";
  const border = isDark ? alpha("#B9C2E0", 0.14) : "#E4E8F1";

  return createTheme({
    palette: {
      mode,
      primary: {
        main: brand.primary,
        light: brand.primaryLight,
        dark: brand.primaryDark,
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: brand.accent,
        light: "#5AD4DC",
        dark: "#0D8992",
        contrastText: "#FFFFFF",
      },
      button: {
        darker: brand.primaryDark,
        dark: brand.primary,
      },
      accentText: isDark ? brand.primaryLight : brand.primaryDark,
      semanticText: {
        success: isDark ? "#5DD5A6" : "#0C7951",
        error: isDark ? "#FF7A7E" : "#B72D32",
        warning: isDark ? "#FFC967" : "#8A5700",
        info: isDark ? "#77B0FF" : "#2869C1",
      },
      table: {
        head: isDark ? "#192238" : "#F7F8FC",
        row: paper,
      },
      background: {
        default: background,
        paper,
      },
      text: {
        primary: isDark ? "#F4F6FC" : "#172033",
        secondary: isDark ? "#AAB3CA" : "#667085",
      },
      divider: border,
      success: {
        main: "#17A673",
        light: "#5DD5A6",
        dark: "#0C7951",
      },
      error: {
        main: "#E5484D",
        light: "#FF7A7E",
        dark: "#B72D32",
      },
      warning: {
        main: "#F5A524",
        light: "#FFC967",
        dark: "#8A5700",
      },
      info: {
        main: "#3E8BF3",
        light: "#77B0FF",
        dark: "#2869C1",
      },
      grey: {
        300: isDark ? "#313B52" : "#D5DAE5",
        700: isDark ? "#9DA7BF" : "#475467",
        900: isDark ? "#F4F6FC" : "#172033",
      },
    },
    shape: {
      borderRadius: 14,
    },
    typography: {
      fontFamily:
        'Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      h1: { fontWeight: 760, letterSpacing: "-0.04em" },
      h2: { fontWeight: 740, letterSpacing: "-0.035em" },
      h3: { fontWeight: 720, letterSpacing: "-0.03em" },
      h4: { fontWeight: 720, letterSpacing: "-0.025em" },
      h5: { fontWeight: 700, letterSpacing: "-0.02em" },
      h6: { fontWeight: 680, letterSpacing: "-0.015em" },
      button: {
        fontWeight: 650,
        letterSpacing: "-0.005em",
        textTransform: "none",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "*": { boxSizing: "border-box" },
          "*::selection": {
            backgroundColor: alpha(brand.primary, 0.22),
          },
          body: {
            backgroundColor: background,
            color: isDark ? "#F4F6FC" : "#172033",
          },
          "*::-webkit-scrollbar": { width: 8, height: 8 },
          "*::-webkit-scrollbar-track": { background: "transparent" },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: isDark ? "#35405A" : "#CBD2DF",
            borderRadius: 999,
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${border}`,
            boxShadow: isDark
              ? "0 18px 45px rgba(0, 0, 0, 0.18)"
              : "0 16px 40px rgba(30, 42, 74, 0.07)",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${border}`,
            borderRadius: 18,
            backgroundImage: "none",
            boxShadow: isDark
              ? "0 14px 32px rgba(0, 0, 0, 0.16)"
              : "0 12px 30px rgba(31, 42, 68, 0.07)",
            transition:
              "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            minHeight: 42,
            borderRadius: 12,
            paddingInline: 18,
            transition:
              "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease",
            "&:active": { transform: "translateY(1px)" },
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${brand.primaryLight} 0%, ${brand.primary} 58%, ${brand.primaryDark} 100%)`,
            boxShadow: `0 9px 20px ${alpha(brand.primary, 0.24)}`,
            "&:hover": {
              boxShadow: `0 12px 26px ${alpha(brand.primary, 0.32)}`,
              transform: "translateY(-1px)",
            },
          },
          outlined: {
            borderColor: border,
            backgroundColor: alpha(paper, 0.62),
            "&:hover": {
              borderColor: alpha(brand.primary, 0.5),
              backgroundColor: alpha(brand.primary, 0.07),
            },
          },
          outlinedPrimary: {
            // brand.primary only reaches 4.03:1 on the dark surface
            color: isDark ? brand.primaryLight : brand.primaryDark,
          },
          textPrimary: {
            color: isDark ? brand.primaryLight : brand.primaryDark,
          },
          outlinedError: {
            color: isDark ? "#FF7A7E" : "#B72D32",
          },
          textError: {
            color: isDark ? "#FF7A7E" : "#B72D32",
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 11,
            transition: "background-color 160ms ease, transform 160ms ease",
            "&:hover": {
              backgroundColor: alpha(brand.primary, 0.09),
            },
            "&:active": { transform: "scale(0.96)" },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: isDark ? alpha("#FFFFFF", 0.035) : "#FFFFFF",
            transition: "box-shadow 160ms ease, background-color 160ms ease",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: border,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(brand.primary, 0.52),
            },
            "&.Mui-focused": {
              boxShadow: `0 0 0 4px ${alpha(brand.primary, 0.12)}`,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: 1,
            },
            // Chrome paints autofilled fields with its own background (blue in
            // dark mode) and ignores `background` — only an inset shadow
            // overrides it. The rule lives on root and targets the nested input
            // so the selector outweighs a single class and the browser fill
            // cannot win.
            //
            // The colour matches the field itself: paper plus the same 3.5%
            // white the root adds in dark mode.
            "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active":
              {
                boxShadow: `0 0 0 1000px ${
                  isDark ? "#1A2132" : "#FFFFFF"
                } inset`,
                WebkitTextFillColor: isDark ? "#F4F6FC" : "#172033",
                caretColor: isDark ? "#F4F6FC" : "#172033",
                borderRadius: "inherit",
              },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: isDark ? "#9FA9C1" : "#667085",
            "&.Mui-focused": { color: brand.primary },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 620,
            borderColor: border,
          },
          // Нейтральный фон — только для чипов без цвета. Раньше он ставился
          // всем залитым, включая цветные, а текст MUI продолжал красить в
          // contrastText: белым по почти белому, контраст 1.06:1.
          filled: ({ ownerState }: any) => ({
            ...(ownerState?.color === "default" || !ownerState?.color
              ? { backgroundColor: isDark ? "#1B253B" : "#EEF1F7" }
              : {}),
          }),
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            border: `1px solid ${border}`,
            boxShadow: "none",
            backgroundColor: paper,
            scrollbarGutter: "stable",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: border,
            paddingBlock: 13,
          },
          head: {
            backgroundColor: isDark ? "#192238" : "#F7F8FC",
            color: isDark ? "#C7CEE0" : "#475467",
            fontSize: 12,
            fontWeight: 720,
            letterSpacing: "0.035em",
            textTransform: "uppercase",
            boxShadow: `inset 0 -1px 0 ${border}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: "background-color 140ms ease",
            "&:hover": {
              backgroundColor: `${alpha(brand.primary, 0.045)} !important`,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 22,
            border: `1px solid ${border}`,
            boxShadow: "0 28px 80px rgba(10, 16, 34, 0.24)",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontWeight: 720, padding: "22px 24px 12px" },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { borderRadius: "20px 0 0 20px" },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            padding: 4,
            gap: 4,
            borderRadius: 13,
            border: `1px solid ${border}`,
            backgroundColor: isDark ? alpha("#FFFFFF", 0.035) : "#FFFFFF",
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            border: "0 !important",
            borderRadius: "9px !important",
            padding: "8px 14px",
            color: isDark ? "#AAB3CA" : "#667085",
            fontWeight: 650,
            textTransform: "none",
            "&.Mui-selected": {
              color: isDark ? brand.primaryLight : brand.primaryDark,
              backgroundColor: alpha(brand.primary, 0.12),
            },
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 650,
          },
          outlinedPrimary: {
            "&.Mui-selected": {
              color: isDark ? brand.primaryLight : brand.primaryDark,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            marginTop: 8,
            borderRadius: 14,
            border: `1px solid ${border}`,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            margin: "3px 6px",
            borderRadius: 9,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 12, alignItems: "center" },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 12,
          },
        },
      },
    },
  });
};

export const lightTheme = buildTheme("light");
export const darkTheme = buildTheme("dark");
